/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-explicit-any */
import PaymentForm from '@alinta-digital/payment-form';
import { PaymentFormProps } from '@alinta-digital/payment-form/PaymentForm';
import { PaymentType } from '@alinta-digital/payment-form/types';
import {
  BamboraPaymentDetails,
  PaymentConfirmationProps,
  PaymentPrefilledDetailsProps
} from '@alinta-digital/payment-form/view';
import { ServerError, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { FC, useEffect, useRef, useState } from 'react';
import { PaymentInput, PaymentMethodType, PaymentRequestStripe } from '../../api/PaymentController.types';
import { PaymentSource, PaymentTokenType, PaymentTransactionType, WebsitePaymentType } from '../../api/PaymentEnums';
import {
  AppInsightInput,
  clearLocalStore,
  GENERATE_PAYPAL_CAPTURE_ORDER_WEB,
  GENERATE_PAYPAL_ORDER_ID_WEB,
  GeneratePaypalOrderIdWebResponse,
  GET_PAYPAL_RESPONSE,
  GET_RECEIPT_NUMBER,
  getAccountNumber,
  getAmountLocal,
  LOG_APP_INSIGHT,
  LogAppInsightResponse,
  MAKE_SINGLE_PAYMENT_MUTATION,
  MAKE_SINGLE_PAYMENT_MUTATION_STRIPE,
  PayPalResultDTO,
  setAccountNumberLocal,
  setAmountLocal
} from '../../api/Payments.gql';
import { VALIDATE_ACCOUNT } from '../../api/Account.gql';
import PaymentLoading from '../PaymentLoading';
import { analyticsHelperWebPayment } from '@alinta-digital/payment-form/utils/analytics';
import { useFeatureFlag } from '../../api/useFeatureFlag';
import InfoBanner from '../InfoBanner/InfoBanner';
import { format, parseISO } from 'date-fns';
import { pushAnalyticsBase } from '@alinta-digital/myaccount-frontend-shared';
import { EmailAddress } from '@alinta-digital/payment-form/components';
import {
  AnalyticsActionType,
  AnalyticsEventNames,
  CCPaymentReceipt
} from '@alinta-digital/myaccount-frontend-shared/types';
import { useStripe } from '@stripe/react-stripe-js';

const Payment: FC<PaymentFormProps> = (props) => {
  const { paymentDetails, paymentSummary, paymentConfirmation }: PaymentFormProps = props;

  const [onceOffPaymentSuccess, setOnceOffPaymentSuccess] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState('');
  const [paidAmount, setpaidAmount] = useState(0);
  const [fuel, setFuel] = useState('');
  const [accountNumber, setAccountNumber] = useState(0);
  const [postcode, setPostcode] = useState('');
  const [paymentRegion, setPaymentRegion] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [payloadRequest, setPayLoadRequest] = useState<PaymentRequestStripe | null>(null);

  const [isAccountNumberValid, setIsAccountNumberValid] = useState(false);
  const [message, setMessage] = useState('');
  const [paypalPaymentSuccess, setPaypalPaymentSucces] = useState(false);
  const [paymentType, setPaymentType] = useState(WebsitePaymentType.CARD);
  const [hasPayPalResponse, setHasPaypalResponse] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const isPaypalResultPage = urlParams?.has('SessionID') && urlParams.has('SST');
  const paymentPrefilledDetails: PaymentPrefilledDetailsProps = {
    accountNumber: urlParams.get('accountNumber') !== null ? parseInt(urlParams.get('accountNumber')!) : undefined,
    paymentAmount: urlParams.get('amount') || undefined,
    postcode: urlParams.has('postcode') && urlParams.get('postcode') !== null ? urlParams.get('postcode')! : undefined
  };
  const { data: paymentOutageData } = useFeatureFlag('MYA_PaymentOutage');
  const paymentOutage = paymentOutageData?.featureValue;
  const { data: StripeFeatureEnable } = useFeatureFlag('MyA_IsStripeEnabled');

  const isStripeEnable = StripeFeatureEnable?.featureValue;
  const { data: featureDataApple } = useFeatureFlag('MyA_IsApplePayEnabled');
  const { data: featureDataGoogle } = useFeatureFlag('MyA_IsGooglePayEnabled');
  const { data: featureDataPaypal } = useFeatureFlag('MyA_IsPaypalEnabled');
  const { data: featurePostcodeValidation } = useFeatureFlag('Website_PostcodeValidation');
  const isApplePayEnabled = featureDataApple?.featureValue;
  const isGooglePayEnabled = featureDataGoogle?.featureValue;
  const isPaypalEnabled = featureDataPaypal?.featureValue;
  const [makePayment, { loading: loadingExpress }] = useMutation(MAKE_SINGLE_PAYMENT_MUTATION_STRIPE);
  const [expressPayload, setExpressPayload] = useState<any>(null);
  const [isExpressPaymentDone, setisExpressPaymentDone] = useState(false);
  const [isPaypalLoading, setisPaypalLoading] = useState(false);
  const proceedToNextStepRef = useRef<(() => void) | null>(null);

  const isPaypalResultPageAfterOrderSubmission = urlParams?.has('token') || urlParams.has('PayerID');

  const [logAppInsight, { data, loading: LoginsightLoading, error }] = useMutation<
    LogAppInsightResponse,
    { input: AppInsightInput }
  >(LOG_APP_INSIGHT);

  useQuery<PayPalResultDTO>(GET_PAYPAL_RESPONSE, {
    skip: !isPaypalResultPage || hasPayPalResponse,
    variables: {
      sessionID: urlParams.get('SessionID') ?? '',
      secureSessionToken: urlParams.get('SST') ?? ''
    },
    fetchPolicy: 'cache-first',
    onCompleted: (result) => {
      if (!result.getPaypalResponseWebsite) {
        setPaymentType(WebsitePaymentType.PAYPAL);
        setHasPaypalResponse(true);
        return;
      }

      setPaymentReceipt(result.getPaypalResponseWebsite.receipt ? result.getPaypalResponseWebsite.receipt : '');
      setPaypalPaymentSucces(result.getPaypalResponseWebsite.paypalTrnID ? true : false);
      setPaymentType(WebsitePaymentType.PAYPAL);
      setHasPaypalResponse(true);
    },
    onError: () => {
      setPaymentType(WebsitePaymentType.PAYPAL);
      setHasPaypalResponse(true);
    }
  });
  const [fetchReceipt] = useLazyQuery(GET_RECEIPT_NUMBER, {
    onCompleted: (data) => {
      const receiptNumber = data?.getReceiptNumberWeb?.receiptNumber;
      setPaymentReceipt(receiptNumber);
    },
    onError: () => {
      setPaymentReceipt('Error fetching receipt');
      pushAnalyticsBase(
        AnalyticsEventNames.PROCESS_PAYMENT,
        'web.bp.pay',
        'gener.error',
        '0',
        AnalyticsActionType.ERROR,
        0
      );
      pushAnalyticsBase(
        AnalyticsEventNames.PROCESS_PAYMENT,
        'web.bp.pay',
        'failure.load',
        '0',
        AnalyticsActionType.LOAD,
        0
      );
    }
  });
  const [getCustomerAccount, { loading, called }] = useLazyQuery(VALIDATE_ACCOUNT, {
    onCompleted: (response) => {
      const { state } = response.customerAccountWebsite.utilityAddress;
      setFuel(response.customerAccountWebsite?.fuel);
      setPaymentRegion(state);
      setIsAccountNumberValid(
        response.customerAccountWebsite && state.length >= 2 && !response.customerAccountWebsite.dormant
      );

      setMessage(
        !response.customerAccountWebsite.dormant
          ? state.length >= 2
            ? 'valid account'
            : 'Fail to get your account data, please refresh the page and try again'
          : 'Invalid account number'
      );
    },
    onError: (response) => {
      setIsAccountNumberValid(false);
      if (response.message.indexOf('400') > 0) {
        setMessage('Invalid account number');
      } else if (response.message.indexOf('500') > 0) {
        setMessage('Server error. Please try again later');
      } else {
        setMessage('Network error. Please check you network and try again');
      }
    },
    fetchPolicy: 'no-cache'
  });

  const [makeOnceOffPaymentMutation] = useMutation(MAKE_SINGLE_PAYMENT_MUTATION, {
    onCompleted: (response) => {
      setOnceOffPaymentSuccess(response.makePaymentWebsite.success);
      setPaymentReceipt(response.makePaymentWebsite.ccPaymentReceipt?.receipt);
      setPaymentType(WebsitePaymentType.CARD);
    },
    onError: (error) => {
      console.error(error);
      setOnceOffPaymentSuccess(false);
    },
    fetchPolicy: 'no-cache'
  });

  const stripe = useStripe();

  async function paymentActionRequired(
    clientSecret: string
  ): Promise<{ isSuccess: boolean; receipt: CCPaymentReceipt | null }> {
    let isSuccess = false;
    let receipt: CCPaymentReceipt | null = null;
    const paymentMethodId = payloadRequest?.paymentMethodId;
    const accountNumber = payloadRequest?.accountNumber;
    if (!clientSecret || !stripe || !paymentMethodId || !accountNumber) {
      return { isSuccess: false, receipt: null };
    }

    try {
      const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId
      });

      if (!paymentIntent || error) {
        return { isSuccess: false, receipt: null };
      }

      if (paymentIntent?.status === 'succeeded') {
        isSuccess = true;
        const receiptResponse = await fetchReceipt({
          variables: {
            paymentIntentId: paymentIntent.id,
            accountNumber
          }
        });
        //fetchReceipt: not provide transaction date and settlement date, so we will use current date for both
        receipt = {
          receipt: receiptResponse.data?.getReceiptNumberWeb?.receiptNumber ?? '',
          transactionDate: new Date(),
          settlementDate: new Date(),
          amount: payloadRequest?.amount || 0
        } as CCPaymentReceipt;
      }
    } catch (error) {
      return { isSuccess: false, receipt: null };
    }

    return { isSuccess, receipt: receipt };
  }

  const [makeOnceOffPaymentStripeMutation] = useMutation(MAKE_SINGLE_PAYMENT_MUTATION_STRIPE, {
    onCompleted: async (response) => {
      const isActionRequired = response?.makeWebStripePayment?.requiresAction;
      const ccReceipt = response?.makeWebStripePayment?.ccPaymentReceipt;
      let isSuccess = isActionRequired ? false : response?.makeWebStripePayment?.success;
      let receipt: CCPaymentReceipt | null = {
        ...(ccReceipt ?? {}),
        transactionDate: ccReceipt?.transactionDate ? new Date(ccReceipt.transactionDate) : null,
        settlementDate: ccReceipt?.settlementDate ? new Date(ccReceipt.settlementDate) : null
      } as CCPaymentReceipt;

      if (isActionRequired) {
        const result = await paymentActionRequired(response?.makeWebStripePayment?.clientSecret);
        isSuccess = result?.isSuccess;
        receipt = result?.receipt;
      }
      setOnceOffPaymentSuccess(isSuccess ?? false);
      setPaymentReceipt(receipt?.receipt ?? '');
      setTransactionDate(receipt?.transactionDate ? receipt.transactionDate.toISOString() : '');
      setPaymentType(WebsitePaymentType.CARD);
      setPayLoadRequest(null);
      setisExpressPaymentDone(true);
      setisPaypalLoading(false);
      if (proceedToNextStepRef.current) {
        proceedToNextStepRef.current();
      }
      const result = await logAppInsight({
        variables: {
          input: {
            params: {
              paymentType: paymentType,
              accountNumber: accountNumber.toString(),
              status: response.statusCode,
              message: 'Public Website PG: Unauthorised'
            }
          }
        }
      });
    },
    onError: async (error) => {
      const networkError = error.networkError as any;
      // Check if content-length is empty in the error response
      if (networkError && networkError.response) {
        const contentLength = networkError.response.headers.get('Content-Length');
        if (contentLength === '0' && networkError.statusCode === 200) {
          setisExpressPaymentDone(true);
          setisPaypalLoading(true);
          if (payloadRequest) {
            makeOnceOffPaymentStripeMutation({
              variables: {
                update: payloadRequest
              }
            });
          }
        } else if (networkError && networkError.statusCode === 428) {
          const response = await logAppInsight({
            variables: {
              input: {
                params: {
                  paymentType: paymentType,
                  accountNumber: accountNumber.toString(),
                  status: networkError.statusCode,
                  message: 'Public Website PG: Human Challenge Required'
                }
              }
            }
          });
          const challenge = networkError.result;
          const overlay = document.getElementById('sec-overlay');
          if (overlay) overlay.style.display = 'block';
          const container = document.getElementById('sec-container');
          if (container) {
            const baseUrl = new URL(challenge.verify_url).origin;
            container.innerHTML = `
                                  <div id="sec-text-container">
                                  <iframe
                                    id="sec-text-if"
                                    title="Challenge Message"
                                    class="custmsg crypto"
                                    scrolling="yes"
                                    src="${baseUrl}${challenge.branding_url_content}"
                                  ></iframe>
                                </div>
                                <div id="sec-if-container">
                                  <iframe
                                    id="sec-cpt-if"
                                    title="Challenge Content"
                                    scrolling="no"
                                    class="crypto"
                                    data-hostname="${baseUrl}"
                                    provider="${challenge.provider}"
                                    data-duration="${challenge.chlg_duration || 10}"
                                    src="${baseUrl}${challenge.chlge_content_url}"
                                    challenge="${btoa(JSON.stringify(challenge))}"
                                  ></iframe>
                                </div>
              `;
          }
        } else {
          setisPaypalLoading(false);
          setOnceOffPaymentSuccess(false);
          if (proceedToNextStepRef.current) {
            proceedToNextStepRef.current();
          }
          pushAnalyticsBase(
            AnalyticsEventNames.PROCESS_PAYMENT,
            'web.bp.pay',
            'gener.error',
            '0',
            AnalyticsActionType.ERROR,
            0
          );
        }
      } else {
        setisPaypalLoading(false);
        setOnceOffPaymentSuccess(false);
        if (proceedToNextStepRef.current) {
          proceedToNextStepRef.current();
        }
        pushAnalyticsBase(
          AnalyticsEventNames.PROCESS_PAYMENT,
          'web.bp.pay',
          'gener.error',
          '0',
          AnalyticsActionType.ERROR,
          0
        );
      }
    },
    fetchPolicy: 'no-cache'
  });
  // const [getOktaTokenMutation] = useMutation(GET_OKTA_TOKEN, {
  //   onCompleted: (response) => {
  //     setToken(response.getOktaToken.data);

  //     if (window && window.location) {
  //       if (isPaypalResultPage && !hasPayPalResponse) {
  //         payWithPayPalResult({
  //           variables: {
  //             sessionID: urlParams?.get('SessionID'),
  //             secureSessionToken: urlParams?.get('SST')
  //           }
  //         });
  //       }
  //     }

  //     // Set the Token timeout so that we refresh it if needed
  //     // setTimeout(
  //     //   () => getOktaTokenMutation({ variables: { input: config.digitalOkta } }),
  //     //   // Allow for time to get a new token...
  //     //   (response.getOktaToken.data.expiresIn + 1) * 1000
  //     // );
  //   },
  //   onError: (error) => {
  //     console.error(error);
  //   },
  //   fetchPolicy: 'no-cache'
  // });

  // useEffect(() => {
  //   getOktaTokenMutation({ variables: { input: config.digitalOkta } });
  // }, [getOktaTokenMutation]);
  const [generatePaypalOrderId] = useMutation<GeneratePaypalOrderIdWebResponse, any>(GENERATE_PAYPAL_ORDER_ID_WEB, {
    onCompleted: (result) => {
      if (!result || !result.generatePaypalOrderIdWeb.status) {
        setOnceOffPaymentSuccess(false);
        pushAnalyticsBase(
          AnalyticsEventNames.PROCESS_PAYMENT,
          'web.bp.pay',
          'gener.error',
          '0',
          AnalyticsActionType.ERROR,
          0
        );
      }
      setAccountNumberLocal(accountNumber.toString());
      const redirectionURL = `${result.generatePaypalOrderIdWeb.approvalUrl}`;
      window.location.href = redirectionURL;
    },
    onError: () => {
      if (expressPayload) {
        expressPayload?.completeStep('step2');
        expressPayload?.goToStep('step3');
        setpaidAmount(expressPayload?.amount || 0);
      }
      setOnceOffPaymentSuccess(false);
      pushAnalyticsBase(
        AnalyticsEventNames.PROCESS_PAYMENT,
        'web.bp.pay',
        'gener.error',
        '0',
        AnalyticsActionType.ERROR,
        0
      );
    },
    fetchPolicy: 'no-cache'
  });
  const [generatePaypalCaptureIdMA] = useMutation(GENERATE_PAYPAL_CAPTURE_ORDER_WEB, {
    onCompleted: (response) => {
      const results = response.generatePaypalCaptureOrderWeb;
      // Handle results here
      setPaypalPaymentSucces(results.success);
      setPaymentReceipt(results.captureId);
      setisExpressPaymentDone(true);
      setPaymentType(WebsitePaymentType.PAYPAL);
      setHasPaypalResponse(true);
      setAccountNumber(parseInt(getAccountNumber()!));
      setpaidAmount(parseFloat(getAmountLocal()!));
      setisPaypalLoading(false);
      clearLocalStore();
      pushAnalyticsBase(
        AnalyticsEventNames.PROCESS_PAYMENT,
        'web.bp.pay',
        'paypalsuc.load',
        '0',
        AnalyticsActionType.LOAD,
        0
      );
    },
    onError: () => {
      setHasPaypalResponse(true);
      setisExpressPaymentDone(true);
      setisPaypalLoading(false);
      setPaymentType(WebsitePaymentType.PAYPAL);
      clearLocalStore();
      setPaypalPaymentSucces(false);
      pushAnalyticsBase(
        AnalyticsEventNames.PROCESS_PAYMENT,
        'web.bp.pay',
        'gener.error',
        '0',
        AnalyticsActionType.ERROR,
        0
      );
      pushAnalyticsBase(
        AnalyticsEventNames.PROCESS_PAYMENT,
        'web.bp.pay',
        'paypalfail.load',
        '0',
        AnalyticsActionType.LOAD,
        0
      );
    },
    fetchPolicy: 'no-cache'
  });
  paymentDetails.onValidateAccountNumber = (updatedAccountNumber: number, updatedPostcode: string | undefined) => {
    if (accountNumber !== updatedAccountNumber || postcode !== updatedPostcode) {
      setAccountNumber(updatedAccountNumber);
      setPostcode(updatedPostcode as string);
      setMessage('');
      const accountNumber = updatedAccountNumber.toString();
      const variables = featurePostcodeValidation.featureValue
        ? {
          accountNumber,
          postcode: updatedPostcode
        }
        : { accountNumber };

      if (
        (featurePostcodeValidation.featureValue && updatedPostcode && updatedPostcode.length < 4) ||
        updatedAccountNumber.toString().length < 9
      )
        setIsAccountNumberValid(false);
      else getCustomerAccount({ variables });
    }
    // No return value, as expected by the type
  };

  paymentSummary.onSubmit = async (bamboraPaymentDetails: BamboraPaymentDetails, proceedToNextStep: () => void) => {
    const paymentDetails: PaymentInput = {
      reference: bamboraPaymentDetails.accountNumber,
      accountNumber: bamboraPaymentDetails.accountNumber,
      paymentTokenType: PaymentTokenType.STT,
      paymentToken: bamboraPaymentDetails.paymentToken,
      transactionType: PaymentTransactionType.PAYMENT,
      amount: bamboraPaymentDetails.amount,
      paymentRegion,
      paymentType: PaymentType.CUSTOM_CHECKOUT,
      source: PaymentSource.WEBSITE,
      emailAddress: bamboraPaymentDetails.emailAddress,
      postcode: bamboraPaymentDetails.postcode
    };
    if (isStripeEnable) {
      setpaidAmount(bamboraPaymentDetails.amount);
      let PaymentDetailsStripe: PaymentRequestStripe = {
        paymentMethodId: bamboraPaymentDetails.ccDetails?.token as string,
        paymentSourceMethod: PaymentMethodType.CC_ADHOC,
        source: PaymentSource.WEBSITE,
        transactionType: 'Payment',
        isSavePayment: false,
        accountNumber: bamboraPaymentDetails.accountNumber,
        amount: bamboraPaymentDetails.amount,
        postcode: bamboraPaymentDetails.postcode
      };
      if (bamboraPaymentDetails.emailAddress && bamboraPaymentDetails.emailAddress !== '') {
        PaymentDetailsStripe = { ...PaymentDetailsStripe, emailAddress: bamboraPaymentDetails.emailAddress };
      }
      proceedToNextStepRef.current = proceedToNextStep;
      setPayLoadRequest(PaymentDetailsStripe);
      await makeOnceOffPaymentStripeMutation({
        variables: {
          update: PaymentDetailsStripe
        }
      });
    } else {
      await makeOnceOffPaymentMutation({
        variables: {
          update: paymentDetails
        }
      });
      proceedToNextStep();
    }
  };
  const capitalizeFirstLetter = (str: string) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const convertTime = (timeStr: string) => {
    if (timeStr) {
      const date = parseISO(timeStr);

      const formattedDate = format(date, 'dd MMMM yyyy');
      const formattedTime = format(date, 'hh:mm a');

      return {
        date: formattedDate,
        time: formattedTime
      };
    }
  };
  useEffect(() => {
    const handleAkamaiChallengeMessage = (event: MessageEvent) => {
      // Optionally check event.origin for security
      let data = event.data;
      // Parse if data is a string
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          // Ignore if not valid JSON
          return;
        }
      }
      if (data && data.state_response === 'done') {
        // Hide overlay if present
        const overlay = document.getElementById('sec-overlay');
        if (overlay) overlay.style.display = 'none';

        // Optionally, retry the payment mutation or proceed with your flow
        if (payloadRequest) {
          setisPaypalLoading(true);
          makeOnceOffPaymentStripeMutation({
            variables: {
              update: payloadRequest
            }
          });
        }
      }
    };

    window.addEventListener('message', handleAkamaiChallengeMessage);

    return () => {
      window.removeEventListener('message', handleAkamaiChallengeMessage);
    };
  }, [payloadRequest, makeOnceOffPaymentStripeMutation]);

  useEffect(() => {
    if (expressPayload && onceOffPaymentSuccess) {
      expressPayload?.completeStep('step2');
      expressPayload?.goToStep('step3');
      setpaidAmount(expressPayload?.amount || 0);
    }
  }, [onceOffPaymentSuccess, paymentReceipt, expressPayload]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const payerId = params.get('PayerID');

    if (token && payerId) {
      setisPaypalLoading(true);
      generatePaypalCaptureIdMA({
        variables: {
          input: {
            accountNumber: getAccountNumber(),
            orderId: token
          }
        }
      });
    } else if (token && !payerId) {
      getAccountNumber() ? (setisExpressPaymentDone(true), clearLocalStore()) : setisExpressPaymentDone(false);
    }
  }, [isPaypalResultPageAfterOrderSubmission, generatePaypalCaptureIdMA]);

  const result = convertTime(transactionDate);
  const updatedPaymentConfirmation: PaymentConfirmationProps = paymentConfirmation;
  paymentConfirmation.success =
    paymentType && paymentType === WebsitePaymentType.PAYPAL ? paypalPaymentSuccess : onceOffPaymentSuccess;
  paymentConfirmation.receiptNumber = paymentReceipt;
  paymentConfirmation.paymentType = paymentType;
  paymentConfirmation.accountNumber = accountNumber.toString();
  paymentConfirmation.payAmount = paidAmount;
  paymentConfirmation.isStripeEnable = isStripeEnable;
  paymentConfirmation.accountType = capitalizeFirstLetter(fuel);
  paymentConfirmation.transactionObj = {
    date: result ? result?.date : '',
    time: result ? result?.time : ''
  };
  paymentConfirmation.showDirectDebitPromo =
    paymentType && paymentType === WebsitePaymentType.PAYPAL ? paypalPaymentSuccess : onceOffPaymentSuccess;
  paymentConfirmation.onSetupDirectDebit = () => {
    window.location.href = 'https://www.alintaenergy.com.au/residential/billing-and-pricing/direct-debit';
  };
  paymentConfirmation.onSubmit = () => {
    setisExpressPaymentDone(false);
    setPaymentType(WebsitePaymentType.CARD);
  };
  const showPaymentsForm =
    (!isPaypalResultPage && paymentType === WebsitePaymentType.CARD) ||
    ((isPaypalResultPage || isPaypalResultPageAfterOrderSubmission) && hasPayPalResponse);

  return (
    <>
      {paymentOutage && (
        <InfoBanner
          backgroundColor="#196CFF"
          textColor="#FFFFFF"
          borderWidth="0px"
          style={{ borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px' }}
        >
          <span style={{ fontWeight: 700, fontSize: '16px' }}>We’re upgrading our payment features this weekend</span>
          <p
            style={{ fontWeight: 400 }}
            dangerouslySetInnerHTML={{
              __html: `The form below is temporarily out of service. Payments can be made via <a href="https://www.alintaenergy.com.au/residential/billing-and-pricing/pay-your-bill#accordion-4148582229-item-276511106c"
              target="_blank" style="color: #FFFFFF !important;">
              BPAY
            </a>`
            }}
          ></p>
        </InfoBanner>
      )}
      {showPaymentsForm && !isPaypalLoading && (
        <PaymentForm
          paymentMethods={{
            enableApplePay: isApplePayEnabled,
            enableGooglePay: isGooglePayEnabled,
            enablePaypal: isPaypalEnabled
          }}
          ga={analyticsHelperWebPayment}
          isExpressPaymentDone={isExpressPaymentDone}
          paymentDetails={{
            ...paymentDetails,
            stripePkkey: paymentDetails.stripePkkey,
            isStripeEnable: isStripeEnable,
            onSubmit: (_dt: any) => {
              if (_dt.event && _dt.event.elementType === 'expressCheckout') {
                setExpressPayload(_dt);
                const updatePayload: PaymentRequestStripe = {
                  accountNumber: accountNumber.toString(),
                  amount: _dt.amount,
                  isSavePayment: false,
                  paymentMethodId: '', // Leave blank here; it’s used after Stripe collects card info
                  paymentSourceMethod:
                    _dt.event.expressPaymentType === 'google_pay'
                      ? PaymentMethodType.GOOGLEPAY_ADHOC
                      : PaymentMethodType.APPLEPAY_ADHOC,
                  source: PaymentSource.WEBSITE,
                  transactionType: 'Payment'
                };
                makePayment({
                  variables: {
                    update: featurePostcodeValidation?.featureValue
                      ? { ...updatePayload, postcode: _dt?.postcode }
                      : updatePayload
                  }
                })
                  .then((result) => {
                    const clientSecret = result.data?.makeWebStripePayment?.clientSecret;
                    if (_dt?.StripeElements && _dt?.stripeInstance) {
                      const elements = _dt?.StripeElements;
                      _dt?.stripeInstance
                        .confirmPayment({
                          clientSecret: clientSecret,
                          elements,
                          confirmParams: {
                            return_url: window.location.href // No return URL
                          },
                          redirect: 'if_required'
                        })
                        .then(function (result: any) {
                          const paymentIntentId = result.paymentIntent.id;
                          if (paymentIntentId) {
                            pushAnalyticsBase(
                              AnalyticsEventNames.PROCESS_PAYMENT,
                              'web.bp.pay',
                              'success.load',
                              '0',
                              AnalyticsActionType.LOAD,
                              0
                            );
                            setOnceOffPaymentSuccess(true);
                            fetchReceipt({
                              variables: {
                                paymentIntentId,
                                accountNumber: accountNumber.toString()
                              }
                            });
                          }
                          if (result.error) {
                            // Inform the customer that there's an error.
                            setOnceOffPaymentSuccess(false);
                          }
                        })
                        .catch((error: any) => {
                          pushAnalyticsBase(
                            AnalyticsEventNames.PROCESS_PAYMENT,
                            'web.bp.pay',
                            'gener.error',
                            '0',
                            AnalyticsActionType.ERROR,
                            0
                          );
                          console.error('Stripe confirmPayment error:', error);
                          setOnceOffPaymentSuccess(false);
                        });
                    }
                  })
                  .catch((err) => {
                    console.error('GraphQL makeStripePayment error:', err);
                    pushAnalyticsBase(
                      AnalyticsEventNames.PROCESS_PAYMENT,
                      'web.bp.pay',
                      'gener.error',
                      '0',
                      AnalyticsActionType.ERROR,
                      0
                    );
                    setOnceOffPaymentSuccess(false);
                  });
              }

              if (_dt.event && _dt.event.type === 'paypal') {
                setExpressPayload(_dt);
                setAmountLocal(_dt.amount.toString());
                const urlWithoutQuery = window.location.origin + window.location.pathname;
                generatePaypalOrderId({
                  variables: {
                    input: {
                      accountNumber: accountNumber.toString(),
                      postcode: _dt.postcode,
                      amount: _dt.amount,
                      clientURL: urlWithoutQuery,
                      paymentSourceMethod: 'PAYPAL_ADHOC',
                      source: PaymentSource.WEBSITE,
                      emailAddress: _dt.emailAddress
                    }
                  }
                });
              }
            }
          }}
          paymentPrefilledDetails={paymentPrefilledDetails}
          paymentSummary={paymentSummary}
          paymentConfirmation={updatedPaymentConfirmation}
          isUnAuthenticated={true}
          withPostcodeValidation={featurePostcodeValidation.featureValue}
          isValidAccount={isAccountNumberValid}
          accountValidationLoading={loading}
        />
      )}
      {(!showPaymentsForm || isPaypalLoading) && <PaymentLoading />}
    </>
  );
};

export default Payment;
